import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

// Import logo
import logo from '../assets/images/logo_laprecio_navy_gold_transparent.png';

/**
 * Public layout component for the landing page and other public pages
 */
const PublicLayout = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Minerals', path: '/minerals' },
    { name: 'Digital Trust', path: '/digital-trust' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
    { name: 'FAQ', path: '/faq' }
  ];

  const drawer = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            component={RouterLink}
            to={item.path}
            key={item.name}
          >
            <ListItemText primary={item.name} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {/* Login disabled until platform is ready */}
        <ListItem
          button
          component={RouterLink}
          to="/register"
        >
          <ListItemText primary="Register Interest" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="transparent" elevation={0} sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)', backgroundColor: 'white', py: 0.5 }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1, px: 1 }}> {/* Reduced vertical padding */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                mr: 4, // Increased margin to prevent overlap
                flexGrow: 0, // Changed from 1 to 0 to prevent pushing nav items
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                minWidth: '200px', // Ensure minimum width for logo area
              }}
            >
              <div className="logo-container" style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/laprecio-logo.png"
                  alt="Laprecio Logo"
                  style={{
                    height: 'auto',
                    width: 'auto',
                    maxHeight: 78, // Increased by 30% from 60
                    maxWidth: '100%',
                    padding: '0',
                    backgroundColor: 'transparent',
                    objectFit: 'contain',
                    verticalAlign: 'middle',
                    imageRendering: 'high-quality',
                    filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.2))'
                  }}
                  className="logo-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/200x80/f5e5a5/ca8a04?text=LAPRECIO';
                  }}
                />
              </div>
            </Box>

            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={toggleDrawer(true)}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={toggleDrawer(false)}
                >
                  {drawer}
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'flex-end' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.name}
                    component={RouterLink}
                    to={item.path}
                    sx={{
                      color: 'text.secondary',
                      mx: 0.75, // Reduced horizontal margin to fit all items
                      fontSize: '0.9rem', // Slightly smaller font size
                      fontWeight: 'bold',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    {item.name}
                  </Button>
                ))}

                {/* Login disabled until platform is ready */}
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                  startIcon={<span role="img" aria-label="Register">📝</span>}
                >
                  Register Interest
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      <Box component="footer" sx={{ mt: 'auto' }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default PublicLayout;
